import { IDebuggerMessage } from "@incendium/inc-ts-helpers";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { smartIframeDebug } from "features/smartIFrame/utils/smartIframeDebug";

type ISmartIFrameContext = {
  sendMessage: (msg: IDebuggerMessage) => void;
  iframeRef: React.RefObject<any>;
  productSchemaFound: boolean;
  setProductSchemaFound: Dispatch<SetStateAction<boolean>>;
  iframeReadyVersion: number;
  notifyIframeReady: () => void;
};

export const SmartIFrameContext = createContext<ISmartIFrameContext | null>(
  null
);
export const useSmartIframeContext = () =>
  useContext(SmartIFrameContext) as ISmartIFrameContext;

export const SmartIFrameProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const iframeRef = useRef<any>(null);
  const messageQueue = useRef<IDebuggerMessage[]>([]);
  const [productSchemaFound, setProductSchemaFound] = useState(false);
  const [iframeReadyVersion, setIframeReadyVersion] = useState(0);

  const flushMessageQueue = useCallback(() => {
    if (!iframeRef?.current) {
      smartIframeDebug("provider", "flush skipped — iframeRef missing", {
        queued: messageQueue.current.length,
      });
      return;
    }
    const queued = messageQueue.current.length;
    if (queued > 0) {
      smartIframeDebug("provider", `flushing ${queued} queued message(s)`);
    }
    while (messageQueue.current.length > 0) {
      const message = messageQueue.current.shift();
      if (message) {
        smartIframeDebug("provider", "flush → iframe", message);
        iframeRef.current.sendMessage(message);
      }
    }
  }, []);

  const sendMessage = useCallback((message: IDebuggerMessage) => {
    if (!iframeRef?.current) {
      messageQueue.current.push(message);
      smartIframeDebug("provider", "queued (iframeRef missing)", {
        message,
        queueLength: messageQueue.current.length,
      });
      return;
    }
    smartIframeDebug("provider", "send → iframe", message);
    iframeRef.current.sendMessage(message);
  }, []);

  const notifyIframeReady = useCallback(() => {
    flushMessageQueue();
    setIframeReadyVersion((version) => {
      const next = version + 1;
      smartIframeDebug("provider", "iframe ready", {
        previousVersion: version,
        iframeReadyVersion: next,
      });
      return next;
    });
  }, [flushMessageQueue]);

  return (
    <SmartIFrameContext.Provider
      value={{
        sendMessage,
        iframeRef,
        productSchemaFound,
        setProductSchemaFound,
        iframeReadyVersion,
        notifyIframeReady,
      }}
    >
      {children}
    </SmartIFrameContext.Provider>
  );
};
